import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from "react";
import { Capacitor } from "@capacitor/core";
import throttle from "lodash/throttle";
import { useCurrentUser } from "@client/hooks/use-current-user";
import { impersonationRef } from "@client/ImperativeState";
import { trpc } from "@client/trpc";
import { CRITICAL_TRPC_OPTIONS } from "@client/trpcLinks";
import { type SyncFeature } from "@client/sync";
import {
  type ProgressRow,
  watchProgressThrottleMs,
} from "@lib/videoStateTypes";
import {
  applyProgressFlushResult,
  createVideoStatesProgressSync,
  isDuplicateNoOpRejection,
  type ProgressPayload,
} from "./videoStatesProgressSync";

interface VideoStatesContextValue {
  getState: (videoId: string) => ProgressRow | undefined;
  updateProgress: (videoId: string, watchedSeconds: number) => void;
  flushProgress: (videoId: string) => void;
}

const Ctx = createContext<VideoStatesContextValue | null>(null);

export function VideoStatesProvider({
  roundId,
  viewAsUserId,
  children,
}: {
  roundId: string;
  // Included in the query input so React Query treats different
  // impersonation targets as distinct cache entries. The server scopes
  // by `ctx.getUser()` independently; this is purely a client-side
  // cache-key discriminator. Without it, switching impersonation
  // targets returns the previous target's cached rows synchronously
  // and `FullModePlayer` can apply a one-shot resume seek for the
  // wrong user before the background refetch lands.
  viewAsUserId: string | null;
  children: ReactNode;
}) {
  const utils = trpc.useUtils();
  const queryInput = useMemo(
    () => ({ roundId, viewAsUserId: viewAsUserId ?? undefined }),
    [roundId, viewAsUserId],
  );
  const query = trpc.getUserVideoState.useQuery(
    queryInput,
    CRITICAL_TRPC_OPTIONS,
  );

  // Render-fresh mirror of the query's fetch state for the outbox drain
  // handler (a closure created in an effect, which would otherwise capture a
  // stale snapshot). Fetch-state transitions re-render, keeping this current.
  const queryFetchingRef = useRef(false);
  queryFetchingRef.current = query.isFetching;

  // Refetch on visibility changes for parity with iPhone hydration.
  const refetch = query.refetch;
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        void refetch();
      }
    };

    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [refetch]);

  const progressMap = useMemo(() => {
    const map = new Map<string, ProgressRow>();

    for (const row of query.data?.progress ?? []) {
      map.set(row.videoId, row);
    }

    return map;
  }, [query.data]);

  // Progress writes go through a durable outbox (queue + retrying worker),
  // not a fire-and-forget mutation: progress accrued OFFLINE must survive
  // until reconnect (and, on native, a force-quit) — see SP-2569, where the
  // old mutation's onError simply refetched server truth and the offline
  // progress was silently discarded. Transient failures stay queued; the
  // worker retries on its tick and drains on reconnect. Two narrow loss
  // paths remain, both accepted: a ClientError drop (the optimistic row
  // then lingers until the next refetch), and — web only — a trailing
  // throttle window enqueued at round exit dies with the session-memory
  // outbox if the tab closes before another round entry (the old mutation
  // sent it immediately when online; native's localStorage queue delivers
  // it next session, and the in-player unload beacon covers tab-close
  // during playback).
  //
  // Impersonation guard, third layer (the other two: the server no-ops
  // writes when effective ≠ real user, and performWrite/flushProgress
  // cancel on target mismatch): the outbox is created and started ONLY for
  // a non-impersonated session, so queued real-user writes can never flush
  // under an acting-as header.
  const { data: currentUser } = useCurrentUser();
  const realUserId = currentUser?.userId ?? null;
  const progressSyncRef = useRef<SyncFeature<ProgressPayload> | null>(null);

  useEffect(() => {
    if (viewAsUserId != null || realUserId == null) {
      return;
    }

    const feature = createVideoStatesProgressSync({
      userId: realUserId,
      isNative: Capacitor.isNativePlatform(),
      // A send in flight when the user switches into impersonation goes out
      // under acting-as headers; the server no-ops it and an ungated flush
      // would dequeue the real user's write as if delivered. Checked at
      // commit time against the LIVE ref (the closure's viewAsUserId is the
      // mount-time value, stale by definition when the flip happens).
      shouldCommit: () => impersonationRef.current == null,
      onSuccess: (result, payload) => {
        let patched = false;
        utils.getUserVideoState.setData(queryInput, (prev) => {
          if (prev == null) {
            return prev;
          }
          patched = true;
          return applyProgressFlushResult(prev, result);
        });
        // The queue is per-user, not per-round, so a drained entry can
        // belong to a different round than the mounted provider. When the
        // server REJECTED the write (LWW: another device holds newer
        // progress), patching only the mounted round's cache would leave
        // the source round's optimistic row stale until its natural
        // refetch — mark every round's video-state cache stale so a return
        // to that round refetches truth instead of resuming from it. But
        // ONLY for an informative rejection: the commit guard's deliberate
        // duplicate re-send is also rejected (same-timestamp LWW), and
        // invalidating on that echo would refetch-storm for no new data.
        if (!result.applied && !isDuplicateNoOpRejection(payload, result)) {
          void utils.getUserVideoState.invalidate();
          return;
        }
        // A drain can beat the mount's initial fetch — the relaunch-after-
        // offline case drains the durable queue immediately. Then either
        // there was nothing to patch yet, or a PRE-drain response still in
        // flight will overwrite the patch when it lands. Either way the
        // mounted round must refetch post-drain truth, or it resumes from
        // the stale pre-offline position until a visibility refetch.
        if (!patched || queryFetchingRef.current) {
          void utils.getUserVideoState.invalidate(queryInput);
        }
        // An applied drain whose entry came from a DIFFERENT round than the
        // mounted provider (trailing teardown writes drain wherever the user
        // is now) also corrects nothing in its source round's cache — mark
        // that round stale so returning to it within React Query's fresh
        // window refetches post-drain truth. Legacy queued entries without
        // the source stamp fall back to invalidating every round once.
        if (result.applied) {
          if (payload.sourceRoundId == null) {
            void utils.getUserVideoState.invalidate();
          } else if (payload.sourceRoundId !== queryInput.roundId) {
            void utils.getUserVideoState.invalidate({
              roundId: payload.sourceRoundId,
              viewAsUserId: undefined,
            });
          }
        }
      },
    });

    progressSyncRef.current = feature;
    feature.start();

    return () => {
      // Stop the worker, but KEEP the ref pointing at the stopped feature:
      // writes still arrive AFTER this cleanup — the throttle-teardown
      // effect below flushes pending windows (same-component cleanups run
      // in declaration order), and the players' own unmount flushes run
      // after that (React tears down deleted trees parent-first). Enqueue
      // on a stopped feature is safe and durable — it writes the per-user
      // queue (localStorage on native, the module-level web outbox on web)
      // and the kick is a no-op — so the next started worker over that
      // queue (next round entry, or next app session on native) delivers
      // it. Nulling the ref here instead silently dropped up to a full
      // throttle window of progress on every round exit.
      feature.stop();
    };
  }, [viewAsUserId, realUserId, queryInput, utils]);

  const performWrite = useCallback(
    (videoId: string, watchedSeconds: number) => {
      if (viewAsUserId != null) {
        return;
      }

      // No outbox yet (user still resolving at boot): skip. The throttle
      // re-fires within a window, and an optimistic row without a queued
      // write would lie about durability.
      const feature = progressSyncRef.current;
      if (feature == null) {
        return;
      }

      const clientUpdatedAt = new Date().toISOString();

      utils.getUserVideoState.setData(queryInput, (prev) => {
        if (prev == null) {
          return prev;
        }

        const idx = prev.progress.findIndex((r) => r.videoId === videoId);
        const optimistic: ProgressRow = {
          videoId,
          watchedSeconds,
          clientUpdatedAt,
        };

        const nextProgress =
          idx === -1
            ? [...prev.progress, optimistic]
            : prev.progress.map((row, i) => (i === idx ? optimistic : row));

        return { ...prev, progress: nextProgress };
      });

      feature.enqueue({
        videoId,
        watchedSeconds,
        clientUpdatedAt,
        sourceRoundId: queryInput.roundId,
      });
    },
    [queryInput, utils, viewAsUserId],
  );

  const throttledByVideo = useRef(
    new Map<string, ReturnType<typeof throttle>>(),
  );

  const performWriteRef = useRef(performWrite);
  performWriteRef.current = performWrite;

  const updateProgress = useCallback(
    (videoId: string, watchedSeconds: number) => {
      let entry = throttledByVideo.current.get(videoId);

      if (entry == null) {
        entry = throttle(
          (seconds: number) => performWriteRef.current(videoId, seconds),
          watchProgressThrottleMs,
          { leading: false, trailing: true },
        );
        throttledByVideo.current.set(videoId, entry);
      }

      entry(watchedSeconds);
    },
    [],
  );

  const flushProgress = useCallback(
    (videoId: string) => {
      const entry = throttledByVideo.current.get(videoId);

      if (entry == null) {
        return;
      }

      if ((impersonationRef.current ?? null) !== (viewAsUserId ?? null)) {
        entry.cancel();
        return;
      }

      entry.flush();
    },
    [viewAsUserId],
  );

  const viewAsUserIdRef = useRef(viewAsUserId);
  viewAsUserIdRef.current = viewAsUserId;

  useEffect(() => {
    const throttled = throttledByVideo.current;

    return () => {
      // Flush, don't cancel. React tears down a deleted subtree
      // parent-first, so when the whole round unmounts (SPA navigation
      // out of the round) this cleanup runs BEFORE the players' unmount
      // flushProgress calls — cancelling here would silently drop up to
      // a full throttle window (~30s) of watch progress, and no
      // beforeunload beacon fires on SPA navigation. Cancel only when
      // the impersonation target changed, mirroring flushProgress: an
      // impersonated session must never commit pending writes.
      const impersonationMismatch =
        (impersonationRef.current ?? null) !==
        (viewAsUserIdRef.current ?? null);

      for (const entry of throttled.values()) {
        if (impersonationMismatch) {
          entry.cancel();
        } else {
          entry.flush();
        }
      }
      throttled.clear();
    };
  }, []);

  const getState = useCallback(
    (videoId: string) => progressMap.get(videoId),
    [progressMap],
  );

  const value = useMemo<VideoStatesContextValue>(
    () => ({ getState, updateProgress, flushProgress }),
    [getState, updateProgress, flushProgress],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useVideoStates(): VideoStatesContextValue | null {
  return useContext(Ctx);
}
